import { isLiveRoomId } from "@/lib/api/live-api";

/**
 * « Cet onglet est entré dans ce récital par sa salle d'attente. »
 *
 * # Pourquoi c'est retenu
 *
 * Le lien d'un récital à partager est sa page, `/live/recital/<id>` : c'est là
 * qu'on s'inscrit, qu'on voit « C'est parti », et qu'on fait le clic qui
 * réveille le son. Dans le studio, l'adresse porte l'identifiant du récital
 * et non la salle, pour que l'interprète puisse copier sa barre d'adresse.
 *
 * Mais quelqu'un qui ouvre cette adresse sans être passé par la salle
 * d'attente doit y être renvoyé. La salle d'attente note donc ici, en entrant,
 * quel récital et quelle salle ; le studio ne rejoint un récital que si cet
 * onglet l'y a fait entrer. Stockage de session : un rechargement de l'onglet
 * y ramène, un autre navigateur ou un lien copié repasse par la salle.
 */

const KEY = "virtuosa.live.recital-entry";

interface RecitalEntry {
  recitalId: string;
  room: string;
}

function storage(): Storage | null {
  try {
    return typeof globalThis.sessionStorage === "undefined" ? null : globalThis.sessionStorage;
  } catch {
    return null;
  }
}

/** La salle d'attente fait entrer cet onglet dans ce récital, dans cette salle. */
export function rememberRecitalEntry(recitalId: string, room: string): void {
  try {
    storage()?.setItem(KEY, JSON.stringify({ recitalId, room } satisfies RecitalEntry));
  } catch {
    // Stockage refusé : l'entrée échouera vers la salle d'attente, jamais
    // vers un studio sans billet.
  }
}

/** La salle de ce récital si cet onglet y est entré par la salle d'attente, `null` sinon. */
export function recitalEntryRoom(recitalId: string): string | null {
  try {
    const raw = storage()?.getItem(KEY) ?? null;

    if (raw === null) {
      return null;
    }

    const entry = JSON.parse(raw) as Partial<RecitalEntry>;

    if (entry.recitalId !== recitalId || typeof entry.room !== "string" || !isLiveRoomId(entry.room)) {
      return null;
    }

    return entry.room;
  } catch {
    return null;
  }
}

/** On a quitté le récital : rouvrir son adresse repassera par la salle d'attente. */
export function forgetRecitalEntry(): void {
  try {
    storage()?.removeItem(KEY);
  } catch {
    // Rien à faire : l'entrée restera, et ne vaut que pour cet onglet.
  }
}

/**
 * « Cet onglet a été exclu de ce récital. »
 *
 * L'exclusion vit chez le service, le temps de la séance, et LiveKit sort la
 * personne sans un mot : c'est son studio qui lit le motif de la coupure. La
 * salle d'attente, elle, n'en sait rien — l'API ne retient pas les exclusions
 * — et proposerait « Entrer dans la salle » à qui vient d'en sortir, pour l'en
 * ressortir deux secondes plus tard. On le note donc ici, par onglet, et la
 * salle d'attente le dit à la place de la porte. Ce n'est pas une garde : le
 * service ressort qui revient, quel que soit l'onglet.
 */
const EXCLUDED_KEY = "virtuosa.live.excluded";

export function rememberExclusion(recitalId: string): void {
  try {
    storage()?.setItem(EXCLUDED_KEY, recitalId);
  } catch {
    // Stockage refusé : la porte se proposera, et le service ressortira.
  }
}

export function excludedFrom(recitalId: string): boolean {
  try {
    return storage()?.getItem(EXCLUDED_KEY) === recitalId;
  } catch {
    return false;
  }
}
